import { accentColor, BASIC_COLOR_SET } from 'constants/chartConstants';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import React from 'react';

interface VerticalBarChartProps {
  data: {
    campaigns: string;
    accounts: number;
    highlyLikely?: number;
    highlyLikelyWithNoCampaigns?: number;
  }[];
}

const series1Color = BASIC_COLOR_SET[0];
const series2Color = BASIC_COLOR_SET[1];
const series2SelectedColor = accentColor; // Custom color for the selected point

const AccountsCampaignsTrendChart = ({ data }: VerticalBarChartProps) => {
  // Transform to highlight accounts with no campaigns
  const transformedData = data.map((entry) => {
    if (entry.campaigns === '0') {
      // Rename 'highlyLikely' to 'highlyLikelyWithNoCampaigns'
      const { highlyLikely, ...rest } = entry; // Destructure to remove 'highlyLikely'
      return {
        ...rest,
        highlyLikelyWithNoCampaigns: highlyLikely, // Add the renamed property
      };
    }
    return entry;
  });

  const chartOptions = React.useMemo(() => {
    return {
      chart: {
        type: 'column',
        style: {
          fontFamily: 'Noto Sans, sans-serif',
          fontSize: 'clamp(12px, 14px, 1.5vw)',
        },
      },
      credits: {
        enabled: false,
      },
      accessibility: {
        enabled: false,
      },
      title: {
        text: 'Number of Accounts vs Campaigns',
        style: {
          fontSize: 'clamp(12px, 14px, 1.5vw)',
        },
      },
      plotOptions: {
        series: {
          states: {
            inactive: {
              opacity: 1,
            },
            hover: {
              enabled: false,
            },
          },
          point: {
            events: {
              mouseOver: function (this: Highcharts.Point) {
                const chart = this.series.chart;
                // Add plot band to highlight the background
                chart.xAxis[0].addPlotBand({
                  id: 'highlight-band', // Unique ID to remove later
                  from: this.x - 0.5,
                  to: this.x + 0.5,
                  color: 'rgba(200, 200, 200, 0.2)', // Highlight color
                });
              },
              mouseOut: function (this: Highcharts.Point) {
                const chart = this.series.chart;
                // Remove the plot band when not hovering
                chart.xAxis[0].removePlotBand('highlight-band');
              },
            },
          },
        },
        column: {
          stacking: 'normal',
        },
      },
      xAxis: {
        categories: transformedData.map((data) => data.campaigns),
        plotBands: [],
        title: {
          text: 'Number of Campaigns',
        },
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Number of Accounts',
        },
      },
      series: [
        {
          name: 'Accounts',
          data: transformedData.map((data) => data.accounts),
          color: series1Color,
        },
        {
          name: 'Highly Likely Accounts',
          color: series2Color,
          data: transformedData.map((dataRecord, index) => ({
            y: transformedData[index].highlyLikely,
            color: series2Color,
          })),
        },
        {
          name: 'Untargeted Accounts',
          data: transformedData.map((data) => data.highlyLikelyWithNoCampaigns),
          color: series2SelectedColor,
        },
      ],
      tooltip: {
        shared: true,
        pointFormat:
          '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b><br/>',
      },
    };
  }, [data]);

  // TODO: Refactor the component to use the existing reusable VerticalBarChart component
  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export default AccountsCampaignsTrendChart;
